import React from 'react';

import { faSearch, faSliders, faFilter } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { type Table } from '@tanstack/react-table';
import debounce from 'lodash/debounce';
import { useIntl } from 'react-intl';

import { InputWithIcon } from 'src/registry/components/inputs/InputWithIcon';
import { DataTableFilter } from 'src/registry/components/table/DataTableFilter';
import { DataTableSearchInfo } from 'src/registry/components/table/DataTableSearchInfo';
import { tableMessages } from 'src/registry/components/table/utils/messages';
import { Button } from 'src/registry/ui/Button';
import {
    DropdownMenu,
    DropdownMenuCheckboxItem,
    DropdownMenuContent,
    DropdownMenuGroup,
    DropdownMenuLabel,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from 'src/registry/ui/DropdownMenu';

import type {
    DataTableColumnHeaderMeta,
    DataTableNewEntryProps,
    DataTableSemantics,
} from 'src/registry/components/table/types';

export type DataTableControlsProps<TData> = {
    table: Table<TData>;
    semantics?: DataTableSemantics;
    newEntryComponent?: React.FC<DataTableNewEntryProps<TData>>;
    isLoading?: boolean;
    isError?: boolean;
    canRenderSearch?: boolean;
    canRenderViewOptions?: boolean;
    canRenderFilters?: boolean;
    children?: React.ReactNode;
    onFilterSearch?: (columnId: string, value: string) => void;
};

export function DataTableControls<TData>({
    table,
    semantics,
    isError,
    newEntryComponent: NewEntryComponent,
    canRenderSearch,
    canRenderViewOptions,
    canRenderFilters,
    children,
    onFilterSearch,
}: DataTableControlsProps<TData>) {
    const { formatMessage, locale } = useIntl();
    if (isError) return <></>;

    const [openDropdowns, setOpenDropdowns] = React.useState<Record<string, boolean>>({});
    const [localSearch, setLocalSearch] = React.useState(table.getState().globalFilter ?? '');

    React.useEffect(() => {
        // whenever table.globalFilter changes externally, sync it back
        setLocalSearch(table.getState().globalFilter ?? '');
    }, [table.getState().globalFilter]);

    const search = React.useMemo(
        () =>
            debounce((value: string) => {
                table.setGlobalFilter(value);
            }, 500),
        [table],
    );

    canRenderSearch ??= table.getState().globalFilter != null;

    const hideableColumns = table.getAllColumns().filter((column) => column.getCanHide());
    const areAllHideableColumnsVisible = hideableColumns.every((column) => column.getIsVisible());

    canRenderViewOptions ??= hideableColumns.length > 0;

    canRenderFilters ??=
        table
            .getAllColumns()
            .filter(
                (column) =>
                    column.getCanFilter() && !table.options.meta?.columnFiltersShowDropdownState?.includes(column.id),
            ).length > 0;

    const searchFields =
        semantics?.placeholder
            ?.split(',')
            .map((field) => field.trim().toLocaleLowerCase(locale))
            .filter(Boolean) ?? [];

    return (
        <div className="flex w-full flex-wrap gap-4">
            {canRenderSearch && (
                <>
                    <InputWithIcon
                        type="text"
                        name="search"
                        value={localSearch}
                        onChange={(e) => {
                            const value = e.target.value;
                            setLocalSearch(value);
                            search(value);
                        }}
                        wrapperProps={{ className: 'md:max-w-sm grow  md:w-auto w-full ' }}
                        icon={faSearch}
                        placeholder={formatMessage(tableMessages.search)}
                    />
                    {searchFields.length > 0 && <DataTableSearchInfo fields={searchFields} />}
                </>
            )}

            {canRenderViewOptions && (
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button variant="outline" size="xsIcon">
                            <FontAwesomeIcon className="text-primary-700" icon={faSliders} />
                            <span className="xs:block hidden">{formatMessage(tableMessages.view)}</span>
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start" className="w-56">
                        <DropdownMenuLabel>{formatMessage(tableMessages.viewOptions)}</DropdownMenuLabel>
                        <DropdownMenuSeparator />
                        <DropdownMenuGroup>
                            {table
                                .getAllColumns()
                                .filter((column) => column.getCanHide())
                                .map((column) => {
                                    // Since the column can have either a string as a title or has it's title in the
                                    // meta definition of the column we check for either one
                                    const title =
                                        typeof column.columnDef.header === 'string'
                                            ? column.columnDef.header
                                            : (column.columnDef.meta as DataTableColumnHeaderMeta<TData, unknown>)
                                                  ?.title;
                                    return (
                                        <DropdownMenuCheckboxItem
                                            key={`${column.id}-visibility`}
                                            checked={column.getIsVisible()}
                                            onSelect={(e) => e.preventDefault()}
                                            onCheckedChange={(value) => column.toggleVisibility(!!value)}
                                        >
                                            {title}
                                        </DropdownMenuCheckboxItem>
                                    );
                                })}
                        </DropdownMenuGroup>
                        <DropdownMenuSeparator />
                        <DropdownMenuCheckboxItem
                            checked={areAllHideableColumnsVisible}
                            onSelect={(e) => e.preventDefault()}
                            onCheckedChange={() => hideableColumns.forEach((column) => column.toggleVisibility(true))}
                            disabled={areAllHideableColumnsVisible}
                        >
                            {formatMessage(tableMessages.viewEnableAll)}
                        </DropdownMenuCheckboxItem>
                    </DropdownMenuContent>
                </DropdownMenu>
            )}

            {table
                .getAllColumns()
                .filter((column) => table.options.meta?.columnFiltersShowDropdownState?.includes(column.id))
                .map((column) => (
                    <DataTableFilter
                        key={`${column.id}-sort`}
                        column={column}
                        open={!!openDropdowns[column.id]}
                        onOpenChange={(isOpen) => setOpenDropdowns((prev) => ({ ...prev, [column.id]: isOpen }))}
                        setColumnFiltersShowDropdownState={table.options.meta?.setColumnFiltersShowDropdownState}
                        setColumnFiltersState={table.options.meta?.setColumnFiltersState}
                        onFilterSearch={onFilterSearch}
                    />
                ))}

            {canRenderFilters && (
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button variant="outline" className="border-dashed" size="xsIcon">
                            <FontAwesomeIcon icon={faFilter} className="xs:hidden block" />
                            <span className="xs:block hidden">{formatMessage(tableMessages.addFilter)}</span>
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start" className="w-56 scroll-auto">
                        <DropdownMenuLabel>{formatMessage(tableMessages.filterByColumn)}</DropdownMenuLabel>
                        <DropdownMenuSeparator />
                        <DropdownMenuGroup>
                            {table
                                .getAllColumns()
                                .filter(
                                    (column) =>
                                        column.getCanFilter() &&
                                        !table.options.meta?.columnFiltersShowDropdownState?.includes(column.id),
                                )
                                .map((column) => {
                                    // Since the column can have either a string as a title or has it's title in the
                                    // meta definition of the column we check for either one

                                    const title =
                                        (column.columnDef.meta as DataTableColumnHeaderMeta<TData, unknown>)
                                            ?.filterLabel ??
                                        (typeof column.columnDef.header === 'string'
                                            ? column.columnDef.header
                                            : (column.columnDef.meta as DataTableColumnHeaderMeta<TData, unknown>)
                                                  ?.title);

                                    return (
                                        <DropdownMenuItem
                                            onSelect={() => {
                                                if (table.options.meta?.setColumnFiltersShowDropdownState == null)
                                                    return;
                                                table.options.meta?.setColumnFiltersShowDropdownState((prev: any) => [
                                                    ...prev,
                                                    column.id,
                                                ]);
                                                setOpenDropdowns((prevOpen) => ({ ...prevOpen, [column.id]: true }));
                                            }}
                                            key={`${column.id}-enable-sort`}
                                        >
                                            {title}
                                        </DropdownMenuItem>
                                    );
                                })}
                        </DropdownMenuGroup>
                    </DropdownMenuContent>
                </DropdownMenu>
            )}

            {children}

            {NewEntryComponent && <NewEntryComponent table={table} semantics={semantics} />}
        </div>
    );
}
